import { Divider } from "@mui/material";
import { MapPinIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { ResetButton } from "@/components/ResetButton/ResetButton";
import {
  SearchResultButton,
  SearchResultButtonSkeleton,
  SearchResultList,
} from "@/components/SearchResultList/SearchResultList";
import { TextField } from "@/components/TextField/TextField";
import { useHotelsCitiesSearch } from "@/features/hotels/hooks/useHotelsCitiesSearch";
import { TopLocationList } from "@/features/search/components/TopLocationList/TopLocationList";
import { useInputAutoFocus } from "@/hooks/useInputAutoFocus";
import { usePreviousCitySearches } from "@/hooks/usePreviousSearches";
import { topDestinationsQueryOptions } from "@/hooks/useTopLocations";
import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";
import type { City } from "@/types/types";

import { LastSearchedCityList } from "./LastSearchedCityList";

type Props = {
  onChange: (value: City | null) => void;
  analyticsSource: SearchSource;
};

export function DestinationSearch({
  onChange: setValue,
  analyticsSource,
}: Props) {
  const intl = useIntl();
  const { previousSearches, addPreviousSearch } = usePreviousCitySearches();
  const { data: topLocations } = useQuery(topDestinationsQueryOptions());
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isSuccess, error } = useHotelsCitiesSearch(input);

  const handleClick = (value: City) => {
    setValue(value);
  };

  const hasPreviousSearches = previousSearches.length > 0;

  return (
    <div className="flex w-full shrink-0 flex-col">
      <TextField
        fullWidth
        autoComplete="off"
        autoFocus={useInputAutoFocus({
          inputRef,
        })}
        inputControl={{ truncate: true }}
        value={input}
        onChange={(e) => setInput(e.currentTarget.value.slice(0, 30))}
        onFocus={(e) => {
          e.currentTarget.select();
        }}
        inputRef={inputRef}
        startAdornment={<MagnifyingGlassIcon className="fill-stone-700" />}
        endAdornment={
          !!input && (
            <ResetButton
              onClick={() => {
                setInput("");
                inputRef.current?.focus();
              }}
              aria-label={intl.formatMessage({
                defaultMessage: "Clear",
                description:
                  "Generic 'Clear' button label to reset a field or selection",
              })}
            />
          )
        }
        inputLabel={{ hidden: true }}
        placeholder={intl.formatMessage({
          defaultMessage: "Search Destination",
          description: "Hotel destination search input placeholder text",
        })}
      />

      {input.length > 2 ? (
        error || (isSuccess && data.length === 0) ? (
          <div className="mt-6 text-center">
            <div className="mb-1 text-base font-medium">
              {intl.formatMessage({
                defaultMessage: "Nothing found",
                description:
                  "Message when hotel destination search returns no results",
              })}
            </div>
            <div className="text-sm text-text-placeholder">
              {intl.formatMessage({
                defaultMessage: "Please check your input for typos",
                description:
                  "Hint when no destination matches the query, asking user to check for typos",
              })}
            </div>
          </div>
        ) : isSuccess ? (
          <SearchResultList<City>
            data={data}
            getItemKey={(city) => city.id}
            renderItem={(city) => (
              <SearchResultButton
                icon={MapPinIcon}
                primaryText={city.name}
                secondaryText={city.countryName}
                onClick={() => {
                  addPreviousSearch(city);
                  handleClick(city);
                }}
              />
            )}
          />
        ) : (
          <ul className="mt-4 w-full">
            {[...Array(7)].map((_, index) => (
              <li key={index}>
                <SearchResultButtonSkeleton
                  showIconBackground
                  showSecondaryText
                />
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          {hasPreviousSearches ? (
            <LastSearchedCityList
              onClick={(city) => {
                rudderAnalytics.track(
                  HotelSearchTrackingEvents.USER_SELECTED_HOTEL_RECENT_DESTINATION,
                  {
                    source: analyticsSource,
                    location_id: city.id,
                    location_name: city.name,
                    location_type: city.type,
                  },
                );
                handleClick(city);
              }}
            />
          ) : null}

          {topLocations?.length ? (
            <>
              {hasPreviousSearches ? (
                <Divider
                  sx={{
                    mt: "calc(var(--spacing) * 4)",
                    borderColor: "var(--color-stroke-neutral-soft)",
                  }}
                />
              ) : null}
              <TopLocationList<City>
                topLocations={topLocations}
                onClick={(city) => {
                  rudderAnalytics.track(
                    HotelSearchTrackingEvents.USER_SELECTED_HOTEL_TOP_DESTINATION,
                    {
                      source: analyticsSource,
                      location_id: city.id,
                      location_name: city.name,
                      location_type: city.type,
                    },
                  );
                  handleClick(city);
                }}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
